import { AttachmentState } from "@powersync/web"
import { FAILED_PHOTO_CONDITION } from "./attachmentFailureQueries"

/**
 * SQL that attributes queued uploads, parked failures and photo files to a
 * project (issue #209).
 *
 * | Queued item                         | Where its project comes from        |
 * | ----------------------------------- | ----------------------------------- |
 * | PUT on a project-scoped table       | `project_id` in the op data         |
 * | PATCH / DELETE                      | `project_id` kept by trackPrevious  |
 * | PATCH queued before trackPrevious   | the local row, looked up by id      |
 * | `photos` row / photo file           | the photo's link-table row          |
 * | anything else in the same write     | the transaction's other ops         |
 * | `species`, on its own               | none: org-level                     |
 *
 * The last-but-one rule covers a new species saved with the observation that
 * uses it, and a photo deleted together with its link row (both rows are gone
 * by the time the delete is queued).
 */

/** Tables whose rows carry `project_id` directly. */
const PROJECT_TABLES = [
  "observations",
  "survey_sites",
  "collections",
  "determinations",
  "geospatial_data",
  "planned_sites",
  "planned_site_assignments",
  "tracks",
  "observation_photos",
  "survey_site_photos",
  "geospatial_data_photos",
] as const

/** Look up the project of `idExpr`'s row in `tableExpr`'s local table. */
function localProjectLookup(tableExpr: string, idExpr: string): string {
  const branches = PROJECT_TABLES.map(
    (table) =>
      `WHEN '${table}' THEN (SELECT project_id FROM ${table} WHERE id = ${idExpr})`,
  ).join("\n      ")
  return `CASE ${tableExpr}
      ${branches}
      WHEN 'photos' THEN ${photoProjectLookup(idExpr)}
    END`
}

/** The project a photo belongs to, through whichever record it's linked to. */
export function photoProjectLookup(photoIdExpr: string): string {
  return `COALESCE(
      (SELECT COALESCE(op.project_id, o.project_id) FROM observation_photos op
         LEFT JOIN observations o ON o.id = op.observation_id
         WHERE op.photo_id = ${photoIdExpr} LIMIT 1),
      (SELECT COALESCE(ssp.project_id, ss.project_id) FROM survey_site_photos ssp
         LEFT JOIN survey_sites ss ON ss.id = ssp.survey_site_id
         WHERE ssp.photo_id = ${photoIdExpr} LIMIT 1),
      (SELECT project_id FROM geospatial_data_photos
         WHERE photo_id = ${photoIdExpr} LIMIT 1),
      (SELECT project_id FROM local_geospatial_data_photos
         WHERE photo_id = ${photoIdExpr} LIMIT 1)
    )`
}

const PHOTO_LINK_TABLES = `('observation_photos', 'survey_site_photos', 'geospatial_data_photos')`

/** Tables whose rows are items in their own right, as a surveyor sees them. */
const TOP_LEVEL_TABLES = `('observations', 'survey_sites', 'geospatial_data', 'tracks', 'planned_sites')`

/**
 * The record a photo hangs off, through whichever link table holds it. Null
 * once the link row is gone (a photo deleted along with its record).
 */
export function photoParentLookup(photoIdExpr: string): string {
  return `COALESCE(
      (SELECT observation_id FROM observation_photos
         WHERE photo_id = ${photoIdExpr} LIMIT 1),
      (SELECT survey_site_id FROM survey_site_photos
         WHERE photo_id = ${photoIdExpr} LIMIT 1),
      (SELECT geospatial_data_id FROM geospatial_data_photos
         WHERE photo_id = ${photoIdExpr} LIMIT 1),
      (SELECT geospatial_data_id FROM local_geospatial_data_photos
         WHERE photo_id = ${photoIdExpr} LIMIT 1)
    )`
}

/**
 * Every queued change, resolved to the project and the item it belongs to
 * (see ITEMS_BY_PROJECT_SQL for how), and whether that item has been deleted.
 *
 * An item is deleted when the queue holds a DELETE of its own row. Every
 * change queued for it then only exists to make the server match: the
 * DELETEs themselves, and any create still queued because the connector had
 * already sent it when the record was deleted (#211). The upload status
 * leaves those out of what it shows, since the record is already gone from
 * the user's point of view, but they still have to upload.
 */
const QUEUED_OPS_CTE = `
  WITH ops AS MATERIALIZED (
    SELECT
      id AS client_id,
      tx_id,
      json_extract(data, '$.op') AS op,
      json_extract(data, '$.type') AS tbl,
      json_extract(data, '$.id') AS entity_id,
      COALESCE(
        json_extract(data, '$.data.project_id'),
        json_extract(data, '$.old.project_id')
      ) AS direct_project_id,
      COALESCE(
        json_extract(data, '$.data.observation_id'),
        json_extract(data, '$.old.observation_id'),
        json_extract(data, '$.data.survey_site_id'),
        json_extract(data, '$.old.survey_site_id'),
        json_extract(data, '$.data.geospatial_data_id'),
        json_extract(data, '$.old.geospatial_data_id'),
        json_extract(data, '$.data.planned_site_id'),
        json_extract(data, '$.old.planned_site_id')
      ) AS parent_id
    FROM ps_crud
  ),
  resolved AS (
    SELECT
      client_id,
      tx_id,
      tbl,
      entity_id,
      COALESCE(direct_project_id, ${localProjectLookup("tbl", "entity_id")}) AS project_id,
      CASE
        WHEN tbl IN ${TOP_LEVEL_TABLES} THEN entity_id
        WHEN tbl IN ${PHOTO_LINK_TABLES} THEN parent_id
        WHEN tbl = 'planned_site_assignments' THEN COALESCE(
          parent_id,
          (SELECT planned_site_id FROM planned_site_assignments WHERE id = entity_id)
        )
        WHEN tbl = 'photos' THEN ${photoParentLookup("entity_id")}
      END AS item_id
    FROM ops
  ),
  tx_defaults AS (
    SELECT tx_id, MIN(project_id) AS project_id, MIN(item_id) AS item_id
    FROM resolved
    WHERE tx_id IS NOT NULL
    GROUP BY tx_id
  ),
  deleted_items AS (
    SELECT DISTINCT entity_id AS item_id
    FROM ops
    WHERE op = 'DELETE' AND tbl IN ${TOP_LEVEL_TABLES}
  ),
  attributed AS (
    SELECT
      r.client_id,
      r.tbl,
      COALESCE(r.item_id, t.item_id, r.entity_id) AS item_id,
      COALESCE(r.project_id, t.project_id) AS project_id
    FROM resolved r
    LEFT JOIN tx_defaults t ON t.tx_id = r.tx_id
  ),
  queued_ops AS (
    SELECT
      a.*,
      EXISTS (SELECT 1 FROM deleted_items d WHERE d.item_id = a.item_id) AS deleted
    FROM attributed a
  )
`

/**
 * Every queued change, grouped by project, with how many changes are queued
 * ahead of each project's first. Uploads are strictly first in, first out
 * across projects, so `ahead` is how long this project waits behind others.
 * Changes for deleted items are counted apart, in `deletion_ops`.
 */
export const QUEUE_BY_PROJECT_SQL = `
  ${QUEUED_OPS_CTE},
  grouped AS (
    SELECT
      project_id,
      SUM(CASE WHEN NOT deleted AND tbl != 'tracks' THEN 1 ELSE 0 END) AS row_ops,
      SUM(CASE WHEN NOT deleted AND tbl = 'tracks' THEN 1 ELSE 0 END) AS track_ops,
      SUM(CASE WHEN deleted THEN 1 ELSE 0 END) AS deletion_ops,
      MIN(client_id) AS first_client_id
    FROM queued_ops
    GROUP BY project_id
  )
  SELECT
    g.project_id,
    g.row_ops,
    g.track_ops,
    g.deletion_ops,
    g.first_client_id,
    (SELECT COUNT(*) FROM ps_crud c WHERE c.id < g.first_client_id) AS ahead
  FROM grouped g
`

export type QueueByProjectRow = {
  project_id: string | null
  row_ops: number
  track_ops: number
  deletion_ops: number
  first_client_id: number
  ahead: number
}

/**
 * A photo whose upload the server refused for good stays QUEUED_UPLOAD with a
 * row in attachment_failures (#210). It is a failure, not a pending upload,
 * so the pending queries leave it out.
 */
const NOT_PARKED_PHOTO = `NOT EXISTS (SELECT 1 FROM attachment_failures f WHERE f.id = a.id)`

/** Photo files still waiting to upload, by project. */
export const PHOTOS_BY_PROJECT_SQL = `
  SELECT project_id, COUNT(*) AS photos, COALESCE(SUM(size), 0) AS bytes
  FROM (
    SELECT a.size, ${photoProjectLookup("a.id")} AS project_id
    FROM attachments a
    WHERE a.state = ${AttachmentState.QUEUED_UPLOAD}
    AND ${NOT_PARKED_PHOTO}
  )
  GROUP BY project_id
`

export type PhotosByProjectRow = {
  project_id: string | null
  photos: number
  bytes: number
}

/**
 * Parked failures with the project each belongs to. A failure's op data is
 * what was sent: a PUT carries project_id, a PATCH or DELETE doesn't, but
 * saveFailedTransaction keeps its trackPrevious values. Failures parked
 * before that column existed fall back to the local row, then to the other
 * operations parked from the same transaction (saveFailedTransaction stamps
 * them with one `failed_at`).
 */
export const ATTRIBUTED_SYNC_FAILURES_CTE = `
  WITH failures AS (
    SELECT
      sf.*,
      COALESCE(
        json_extract(sf.op_data, '$.project_id'),
        json_extract(sf.previous_values, '$.project_id'),
        ${localProjectLookup("sf.target_table", "sf.entity_id")}
      ) AS resolved_project_id
    FROM sync_failures sf
  ),
  failure_groups AS (
    SELECT failed_at, MIN(resolved_project_id) AS project_id
    FROM failures
    WHERE resolved_project_id IS NOT NULL
    GROUP BY failed_at
  ),
  attributed_failures AS (
    SELECT f.*, COALESCE(f.resolved_project_id, g.project_id) AS project_id
    FROM failures f
    LEFT JOIN failure_groups g ON g.failed_at = f.failed_at
  )
`

/**
 * How many items each project has waiting to upload, as the surveyor counts
 * them: an observation, a site, a map record or a track, and nothing else.
 * Everything that only exists to carry one of those (its photo link rows, the
 * photos rows, the photo files, a species saved along with it) is part of that
 * item, so an observation with one photo is one item from the moment it is
 * saved until its last row and photo file have reached the server.
 *
 * Each queued change and each queued photo file is resolved to the item it
 * belongs to:
 *
 * | Queued change or file          | The item it belongs to                 |
 * | ------------------------------ | -------------------------------------- |
 * | observation, site, map record, |                                        |
 * | track, planned site            | itself                                 |
 * | photo link row                 | the record in its parent id column     |
 * | photos row, photo file         | the record its link row points at      |
 * | planned site assignment        | the planned site it assigns            |
 * | anything else in the same write| the write's other items                |
 * | still nothing                  | itself (e.g. a determination added to  |
 * |                                | an existing collection)                |
 *
 * The same item can appear many times (its row, its link row, its photo
 * file), and appears once in the count. A deleted item isn't counted at all
 * (see QUEUED_OPS_CTE).
 */
export const ITEMS_BY_PROJECT_SQL = `
  ${QUEUED_OPS_CTE},
  queued AS (
    SELECT item_id, project_id
    FROM queued_ops
    WHERE NOT deleted
    UNION ALL
    SELECT
      COALESCE(${photoParentLookup("a.id")}, a.id) AS item_id,
      ${photoProjectLookup("a.id")} AS project_id
    FROM attachments a
    WHERE a.state = ${AttachmentState.QUEUED_UPLOAD}
    AND ${NOT_PARKED_PHOTO}
  ),
  items AS (
    SELECT item_id, MIN(project_id) AS project_id
    FROM queued
    GROUP BY item_id
  )
  SELECT project_id, COUNT(*) AS items
  FROM items
  GROUP BY project_id
`

export type ItemsByProjectRow = {
  project_id: string | null
  items: number
}

/** Failures needing attention by project: parked rows plus refused photos. */
export const FAILURES_BY_PROJECT_SQL = `
  ${ATTRIBUTED_SYNC_FAILURES_CTE}
  SELECT project_id, SUM(n) AS failures FROM (
    SELECT project_id, COUNT(*) AS n FROM attributed_failures GROUP BY project_id
    UNION ALL
    SELECT ${photoProjectLookup("a.id")} AS project_id, 1 AS n
    FROM attachments a
    WHERE ${FAILED_PHOTO_CONDITION}
  )
  GROUP BY project_id
`

export type FailuresByProjectRow = {
  project_id: string | null
  failures: number
}

/** One project's parked failures, plus org-level ones, newest first. */
export const PROJECT_SYNC_FAILURES_SQL = `
  ${ATTRIBUTED_SYNC_FAILURES_CTE}
  SELECT * FROM attributed_failures
  WHERE project_id = ? OR project_id IS NULL
  ORDER BY failed_at DESC
`

/**
 * One project's refused photo files, with the record each is attached to,
 * plus any whose record can't be found (counted under "Other", so they must
 * be listed somewhere).
 */
export const PROJECT_FAILED_PHOTOS_SQL = `
  SELECT * FROM (
    SELECT
      a.id,
      a.size,
      a.timestamp,
      ${photoProjectLookup("a.id")} AS project_id,
      (SELECT observation_id FROM observation_photos WHERE photo_id = a.id LIMIT 1) AS observation_id,
      (SELECT survey_site_id FROM survey_site_photos WHERE photo_id = a.id LIMIT 1) AS survey_site_id,
      COALESCE(
        (SELECT geospatial_data_id FROM geospatial_data_photos WHERE photo_id = a.id LIMIT 1),
        (SELECT geospatial_data_id FROM local_geospatial_data_photos WHERE photo_id = a.id LIMIT 1)
      ) AS geospatial_data_id
    FROM attachments a
    WHERE ${FAILED_PHOTO_CONDITION}
  )
  WHERE project_id = ? OR project_id IS NULL
  ORDER BY timestamp DESC
`

export type FailedPhotoRow = {
  id: string
  size: number | null
  timestamp: number | null
  project_id: string | null
  observation_id: string | null
  survey_site_id: string | null
  geospatial_data_id: string | null
}

/** Parent ids of every record linked to one of the photos in `photoIds`. */
function recordsWithPhotosIn(photoIds: string): string {
  return `
    SELECT observation_id FROM observation_photos WHERE photo_id IN (${photoIds})
    UNION
    SELECT survey_site_id FROM survey_site_photos WHERE photo_id IN (${photoIds})
    UNION
    SELECT geospatial_data_id FROM geospatial_data_photos WHERE photo_id IN (${photoIds})`
}

/**
 * Every record id that still has something waiting to upload: its own row,
 * a photo link row, a photos row, or a photo file. A record only counts as
 * synced once all of those have reached the server.
 *
 * `ps_crud` is read once: each op's JSON (a track's carries its whole line)
 * is parsed in one pass, and the rest works from the extracted columns.
 * MATERIALIZED makes SQLite build `ops` once: without it, it may inline the
 * CTE into each reference and scan ps_crud for every one (needs SQLite
 * 3.35+, which every PowerSync driver here bundles).
 */
export const PENDING_RECORD_IDS_SQL = `
  WITH ops AS MATERIALIZED (
    SELECT
      json_extract(data, '$.type') AS tbl,
      json_extract(data, '$.id') AS id,
      COALESCE(
        json_extract(data, '$.data.observation_id'),
        json_extract(data, '$.old.observation_id'),
        json_extract(data, '$.data.survey_site_id'),
        json_extract(data, '$.old.survey_site_id'),
        json_extract(data, '$.data.geospatial_data_id'),
        json_extract(data, '$.old.geospatial_data_id')
      ) AS parent_id
    FROM ps_crud
  )
  SELECT id FROM ops WHERE id IS NOT NULL
  UNION
  SELECT parent_id FROM ops WHERE tbl IN ${PHOTO_LINK_TABLES}
  UNION
  ${recordsWithPhotosIn(`
    SELECT a.id FROM attachments a
    WHERE a.state = ${AttachmentState.QUEUED_UPLOAD} AND ${NOT_PARKED_PHOTO}
    UNION
    SELECT id FROM ops WHERE tbl = 'photos'`)}
`

/**
 * Every record id the server rejected something for: the record itself, one
 * of its photo links or photos rows, or one of its photo files.
 */
export const FAILED_RECORD_IDS_SQL = `
  SELECT entity_id AS id FROM sync_failures
  UNION
  SELECT COALESCE(
    json_extract(op_data, '$.observation_id'),
    json_extract(previous_values, '$.observation_id'),
    json_extract(op_data, '$.survey_site_id'),
    json_extract(previous_values, '$.survey_site_id'),
    json_extract(op_data, '$.geospatial_data_id'),
    json_extract(previous_values, '$.geospatial_data_id')
  ) FROM sync_failures
  WHERE target_table IN ${PHOTO_LINK_TABLES}
  UNION
  ${recordsWithPhotosIn(`
    SELECT entity_id FROM sync_failures WHERE target_table = 'photos'
    UNION
    SELECT a.id FROM attachments a WHERE ${FAILED_PHOTO_CONDITION}`)}
`
